import auth from '@/services/http/api/auth'
import localStorage from '@/services/storage/localStorage'

import formats from '@/constants/momentFormats'
import token from '@/services/token/index'

const USER_KEY = 'user'

const mState = () => ({
  loader: null,
  user: localStorage.get(USER_KEY, true),
  forcedToLogout: false,
  isRefreshRequestSent: false,
  inactivityModalVisible: {
    modal: false,
    time: 0
  }
})

const mGetters = {
  getLoader: (state) => state.loader,
  getUser: (state) => state.user,
  isLogged: (state) => state.user != null,
  isInactivityModalVisible: (state) => state.inactivityModalVisible,
  timeToLogout(state) {
    return token
      .dayjs()
      .startOf('hour')
      .second(state.inactivityModalVisible.time)
      .format(formats.MINUTE_WITH_SEC)
  },
  isForcedToLogout: (state) => state.forcedToLogout,
  userHasPermission: (state) => (permission) =>
    state.user?.permissions?.includes(permission) ?? false
}

const mMutations = {
  setLoader(state, data) {
    state.loader = data
  },
  setUser(state, data) {
    state.user = data
  },
  setForcedToLogout(state, payload) {
    state.forcedToLogout = payload
  },
  setIsRefreshRequestSent(state, payload) {
    state.isRefreshRequestSent = payload
  },
  setInactivityModalVisible(state, payload) {
    state.inactivityModalVisible = payload
  },
  clearFlags(state) {
    state.inactivityModalVisible = {
      modal: false,
      time: 0
    }
    state.isRefreshRequestSent = false
  }
}

const mActions = {
  login({ dispatch, commit }, payload) {
    commit('setLoader', true)

    return new Promise((resolve, reject) => {
      auth
        .authenticate(payload)
        .then(({ data }) =>
          auth
            .validate(data.token)
            .then(({ headers, data: user }) => {
              commit('setUser', user.data)
              localStorage.set(USER_KEY, user.data, true)

              token.setTokenExpirationTimestamp(headers[token.tokenExpirationHeader])
              dispatch('tokenRegeneration')

              resolve(user)
            })
            .finally(() => {
              commit('setLoader', false)
            })
        )
        .catch((err) => {
          commit('setLoader', false)
          reject(err)
        })
    })
  },
  logout({ commit, getters }) {
    if (getters.getLoader !== true) {
      commit('setLoader', true)
      commit('setUser', null)
      commit('clearFlags')
      token.removeAll()
      localStorage.delete(USER_KEY)
      return auth.logout().finally(() => {
        commit('setLoader', false)
      })
    }
    return null
  },
  refreshToken({ state, commit }) {
    if (state.isRefreshRequestSent === false) {
      commit('setIsRefreshRequestSent', true)
      return auth.refresh().then(({ headers }) => {
        token.setTokenExpirationTimestamp(headers[token.tokenExpirationHeader])
        commit('setIsRefreshRequestSent', false)
      })
    }
    return null
  },
  tokenRegeneration({ getters, commit, dispatch }) {
    token.setLastActivityTimestamp(token.dayjs().unix())
    token.listeners()
    token.startRefreshInterval(() => {
      dispatch('refreshToken')
    })
    token.startTokenGuardInterval(
      () => {
        commit('setForcedToLogout', true)
        dispatch('logout')
      },
      (flag, time) => {
        if (flag === false && getters.isInactivityModalVisible.modal === false) return
        commit('setInactivityModalVisible', {
          modal: flag,
          time
        })
      }
    )
  },
  clearUserOnInvalidToken({ commit }) {
    const isTokenValid = token.isTokenValid()
    if (!isTokenValid) {
      commit('setUser', null)
      commit('clearFlags')
      token.removeAll()
      localStorage.delete(USER_KEY)
    }
  }
}

export default {
  namespaced: true,
  state: mState,
  getters: mGetters,
  mutations: mMutations,
  actions: mActions
}
